const USE_COOKIE_AUTH_KEY = 'useCookieAuth';

export const getCookieValue = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

export const getCsrfTokenFromCookie = (): string | null => getCookieValue('ci_csrf');

/** True on http:// pages (local CRA). SameSite=None cookies without Secure are dropped. */
export const isInsecureHttpOrigin = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.location.protocol === 'http:';
};

export const isCookieAuthEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(USE_COOKIE_AUTH_KEY) === 'true';
  } catch {
    return false;
  }
};

export const getClientAccessToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const trimmed = token.trim();
    if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return null;
    return trimmed;
  } catch {
    return null;
  }
};

/** True when cookie session is preferred or a Bearer token is stored. */
export const hasClientAuthSession = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    if (getClientAccessToken()) return true;
    if (isInsecureHttpOrigin()) return Boolean(getClientAccessToken());
    return isCookieAuthEnabled();
  } catch {
    return false;
  }
};

export const setCookieAuthPreference = (enabled: boolean) => {
  if (typeof window === 'undefined') return;
  try {
    if (enabled) {
      localStorage.setItem(USE_COOKIE_AUTH_KEY, 'true');
      localStorage.removeItem('token');
    } else {
      localStorage.removeItem(USE_COOKIE_AUTH_KEY);
    }
  } catch {
    // ignore storage errors
  }
};

export const clearCookieAuthPreference = () => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(USE_COOKIE_AUTH_KEY);
    localStorage.removeItem('authRole');
  } catch {
    // ignore
  }
};

export const persistSessionRole = (role: string) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem('authRole', role);
  } catch {
    // ignore
  }
};

export const getPersistedSessionRole = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem('authRole');
  } catch {
    return null;
  }
};

export const buildSessionHeaders = (headers: Record<string, string> = {}): Record<string, string> => {
  const next = { ...headers };
  const csrfToken = getCsrfTokenFromCookie();
  if (csrfToken && !next['X-CSRF-Token']) {
    next['X-CSRF-Token'] = csrfToken;
  }
  return next;
};

export const applyAuthSessionFromResponse = (data?: {
  token?: string;
  useCookieAuth?: boolean;
  csrfToken?: string;
  user?: { role?: string };
}) => {
  if (!data) return;
  if (typeof data.user?.role === 'string') {
    persistSessionRole(data.user.role);
  }

  const token = typeof data.token === 'string' && data.token ? data.token : null;
  const wantsCookieAuth = Boolean(data.useCookieAuth);
  // HTTP localhost cannot store SameSite=None cookies without Secure.
  // Keep JWT Bearer there; HTTPS production stays cookie-only when advertised.
  const cookieOnly = wantsCookieAuth && !isInsecureHttpOrigin();

  if (cookieOnly) {
    setCookieAuthPreference(true);
    return;
  }

  // HTTP localhost (or cookie auth off): keep JWT Bearer. Do not clear authRole.
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem('useCookieAuth');
    } catch {
      // ignore
    }
  }
  if (token && typeof window !== 'undefined') {
    try {
      localStorage.setItem('token', token);
    } catch {
      // ignore
    }
  }
};
